// frontend/src/pages/Dashboard.js
import { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Spin, Button, Typography, List, Tag, Progress, Table, Divider, Alert, Space, Select, theme } from 'antd';
import {
    UserOutlined, TeamOutlined, SolutionOutlined, CalendarOutlined, BarChartOutlined,
    PieChartOutlined, CheckCircleOutlined, LoginOutlined, DashboardOutlined, GlobalOutlined,
    HomeOutlined, ReadOutlined, InboxOutlined, DashOutlined, DatabaseOutlined, UpSquareOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAcademicYear } from '../context/AcademicYearContext';
import { getDashboardStats } from '../services/dashboardService';
import { getAllClasses } from '../services/classService';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { PieChart, Pie, Cell, Legend } from 'recharts';
import dayjs from 'dayjs';
import { useNotification } from '../context/NotificationContext';
import { useTheme } from '../context/ThemeContext';
import useResponsive from '../hooks/useResponsive';

const { Title, Text } = Typography;
const { Option } = Select;

// --- CONFIGURATION ---
const DONUT_COLORS = ['#36A2EB', '#FF6384', '#FF9F40', '#4BC0C0', '#9966FF'];

const DashboardPage = () => {
    const { message: notify } = useNotification();
    const { primaryColor } = useTheme();
    const { isMobile } = useResponsive();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState(null);
    const { user } = useAuth();
    const { currentYear } = useAcademicYear();

    useEffect(() => {
        if (currentYear) {
            fetchStats(selectedClass);
        }
    }, [currentYear, selectedClass]);

    useEffect(() => {
        const fetchClasses = async () => {
            try {
                const classesData = await getAllClasses();
                setClasses(classesData);
            } catch (error) {
                notify.error('Failed to load classes for filtering.');
            }
        };
        if (user.role === 'principal' || user.role === 'super_admin') {
            fetchClasses();
        }
    }, [user.role]);

    const fetchStats = async (classId) => {
        setLoading(true);
        try {
            const data = await getDashboardStats(classId);
            setStats(data);
        } catch (error) {
            notify.error('Failed to load dashboard statistics.');
        } finally {
            setLoading(false);
        }
    };

    if (loading || !stats) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}><Spin size="large" /></div>;
    }

    // --- Shared Components ---

    const getEventTagColor = (eventType) => {
        switch (eventType?.toLowerCase()) {
            case 'exam': return 'volcano';
            case 'holiday': return 'green';
            case 'program': return 'geekblue';
            default: return 'default';
        }
    };

    const UpcomingEventsWidget = ({ events }) => (
        <Card title={<><CalendarOutlined /> Upcoming Events</>} bordered={false} style={{ height: '100%' }}>
            {events && events.length > 0 ? (
                <List
                    itemLayout="horizontal"
                    dataSource={events}
                    renderItem={item => (
                        <List.Item>
                            <List.Item.Meta
                                title={<Link to="/calendar">{item.title}</Link>}
                                description={
                                    <>
                                        <Text type="secondary">{dayjs(item.start_date).format('DD MMMM, YYYY')}</Text>
                                        <Tag color={getEventTagColor(item.event_type)} style={{ marginLeft: 8 }}>
                                            {item.event_type || 'Event'}
                                        </Tag>
                                    </>
                                }
                            />
                        </List.Item>
                    )}
                    style={{ maxHeight: 350, overflowY: 'auto' }}
                />
            ) : <Text type="secondary">No upcoming events scheduled.</Text>}
        </Card>
    );

    const RecentLoginsWidget = ({ logins }) => (
        <Card title={<><LoginOutlined /> Recent Logins</>} bordered={false} style={{ height: '100%' }}>
            {logins && logins.length > 0 ? (
                <List
                    itemLayout="horizontal"
                    dataSource={logins}
                    renderItem={item => (
                        <List.Item>
                            <List.Item.Meta
                                avatar={<GlobalOutlined style={{ fontSize: 20, color: primaryColor }} />}
                                title={item.username}
                                description={<Text type="secondary">{dayjs(item.last_login).format('DD MMM YYYY, HH:mm')}</Text>}
                            />
                        </List.Item>
                    )}
                    style={{ maxHeight: 350, overflowY: 'auto' }}
                />
            ) : <Text type="secondary">No recent logins recorded.</Text>}
        </Card>
    );

    const GenderBreakdownWidget = ({ breakdown }) => {
        const data = breakdown.filter(d => d.count > 0);

        return (
            <Card title={<><PieChartOutlined /> Gender Breakdown</>} bordered={false} style={{ height: '100%' }}>
                {data.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                            <Pie
                                data={data}
                                dataKey="count"
                                nameKey="gender"
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={80}
                                paddingAngle={5}
                                labelLine={false}
                            >
                                {data.map((entry, idx) => (
                                    <Cell key={`cell-${idx}`} fill={DONUT_COLORS[idx % DONUT_COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value, name, props) => [`${value} students`, props.payload.gender]} />
                            <Legend layout="horizontal" align="center" verticalAlign="bottom" iconType="circle" />
                        </PieChart>
                    </ResponsiveContainer>
                ) : (
                    <Text type="secondary">No gender data available for students.</Text>
                )}
            </Card>
        );
    };

    const TopStudentsWidget = ({ students, classes, onClassChange, selectedClass }) => (
        <Card
            title={<><UserOutlined /> Top Performing Students</>}
            bordered={false}
            extra={
                <Select
                    allowClear
                    showSearch
                    placeholder="Filter by class"
                    style={{ width: 200 }}
                    onChange={onClassChange}
                    value={selectedClass}
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                >
                    {classes.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                </Select>
            }
        >
            {students && students.length > 0 ? (
                <Table
                    dataSource={students.map((s, idx) => ({ ...s, key: idx, rank: idx + 1 }))}
                    columns={[
                        { title: 'Rank', dataIndex: 'rank', key: 'rank', width: 60, render: (text) => <Text strong>{text}</Text> },
                        { title: 'Name', dataIndex: 'student_name', key: 'student_name' },
                        { title: 'Avg Marks', dataIndex: 'avg_marks', key: 'avg_marks', render: (val) => <Tag color="blue">{Number(val).toFixed(2)}</Tag>, align: 'right' },
                    ]}
                    pagination={false}
                    size="middle"
                />
            ) : <Text type="secondary">No marks recorded yet.</Text>}
        </Card>
    );

    const BottomStudentsWidget = ({ students }) => (
        <Card title={<><UserOutlined style={{ color: '#FF6384' }} /> Areas for Improvement</>} bordered={false}>
            {students && students.length > 0 ? (
                <Table
                    dataSource={students.map((s, idx) => ({ ...s, key: idx, rank: idx + 1 }))}
                    columns={[
                        { title: 'Rank', dataIndex: 'rank', key: 'rank', width: 60, render: (text) => <Text strong>{text}</Text> },
                        { title: 'Name', dataIndex: 'student_name', key: 'student_name' },
                        { title: 'Avg Marks', dataIndex: 'avg_marks', key: 'avg_marks', render: (val) => <Tag color="red">{Number(val).toFixed(2)}</Tag>, align: 'right' },
                    ]}
                    pagination={false}
                    size="middle"
                />
            ) : <Text type="secondary">No marks recorded yet.</Text>}
        </Card>
    );

    // --- Teacher Dashboard ---
    const TeacherDashboard = ({ stats, user }) => {
        const { isClassTeacher, classTeacherClass, subjectsTaught, totalStudentsInClass, attendanceTrend } = stats;

        return (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Header Card */}
                <Card style={{ backgroundColor: primaryColor, color: '#fff' }}>
                    <Row gutter={24} align="middle">
                        <Col xs={24} md={12}>
                            <Title level={3} style={{ color: '#fff', margin: 0 }}>
                                <HomeOutlined /> Teacher Overview
                            </Title>
                            <Text style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                Focused metrics for your assigned students and subjects.
                            </Text>
                        </Col>
                        {isClassTeacher && (
                            <Col xs={24} md={12} style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16 }}>
                                <div>
                                    <Statistic
                                        title={<Text style={{ color: '#fff' }}>Class Teacher Of</Text>}
                                        value={classTeacherClass?.name}
                                        valueStyle={{ color: '#fff', fontSize: '24px' }}
                                    />
                                </div>
                                <Link to={`/classes/${classTeacherClass?.id}`}>
                                    <Button type="primary" style={{ backgroundColor: '#fff', color: primaryColor, border: 'none', fontWeight: 'bold' }}>
                                        Manage My Class
                                    </Button>
                                </Link>
                            </Col>
                        )}
                    </Row>
                </Card>

                {/* Key Metrics */}
                <Row gutter={[24, 24]}>
                    <Col xs={12} md={8}>
                        <Card><Statistic title="Your Students" value={totalStudentsInClass} prefix={<TeamOutlined />} /></Card>
                    </Col>
                    <Col xs={12} md={8}>
                        <Card><Statistic title="Subjects Taught" value={subjectsTaught.length} prefix={<ReadOutlined />} /></Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card title={<Text type="secondary" style={{ fontSize: 14 }}><CheckCircleOutlined /> Last 7 Days Attendance</Text>} style={{ height: '100%' }}>
                            {attendanceTrend.length > 0 ? (
                                <Text strong style={{ fontSize: 24, color: primaryColor }}>
                                    {attendanceTrend[attendanceTrend.length - 1]?.percentage}%
                                </Text>
                            ) : <Text type="secondary">N/A</Text>}
                        </Card>
                    </Col>
                </Row>

                <Row gutter={[24, 24]}>
                    <Col xs={24} lg={16}>
                        {/* Attendance Chart */}
                        <Card title={<><BarChartOutlined /> Your Class Attendance Trend (Last 7 Days)</>} bordered={false}>
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={attendanceTrend} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                                    <defs>
                                        <linearGradient id="colorAttendance" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={primaryColor} stopOpacity={0.8} />
                                            <stop offset="95%" stopColor={primaryColor} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                    <XAxis dataKey="date" tickLine={false} axisLine={false} style={{ fontSize: '12px' }} />
                                    <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tickFormatter={(value) => `${value}%`} style={{ fontSize: '12px' }} />
                                    <Tooltip contentStyle={{ borderRadius: 8, borderColor: '#ccc' }} formatter={(value) => [`${value}%`, 'Attendance']} />
                                    <Area type="monotone" dataKey="percentage" stroke={primaryColor} fillOpacity={1} fill="url(#colorAttendance)" strokeWidth={2} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Card>
                        {/* Subjects Taught List */}
                        <Card title={<><ReadOutlined /> Subjects Assigned</>} style={{ marginTop: 24 }}>
                            {subjectsTaught.length > 0 ? (
                                <Space wrap size={10}>
                                    {subjectsTaught.map((subject, index) => <Tag color="blue" key={index} style={{ fontSize: 14 }}>{subject}</Tag>)}
                                </Space>
                            ) : <Text type="secondary">You are not currently assigned to teach any subjects.</Text>}
                        </Card>
                    </Col>

                    <Col xs={24} lg={8}>
                        <UpcomingEventsWidget events={stats.recentEvents} />
                    </Col>
                </Row>
            </Space>
        );
    };

    // --- Principal Dashboard ---
    const PrincipalDashboard = ({ stats }) => (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Key Metrics */}
            <Row gutter={[24, 24]}>
                <Col xs={12} sm={6}><Card style={{ borderRadius: '12px', border: '1px solid #E2E8F0' }}><Statistic title="Total Students" value={stats.totalStudents} prefix={<TeamOutlined style={{ color: primaryColor }} />} valueStyle={{ color: primaryColor, fontWeight: 700 }} /></Card></Col>
                <Col xs={12} sm={6}><Card style={{ borderRadius: '12px', border: '1px solid #E2E8F0' }}><Statistic title="Total Teachers" value={stats.totalTeachers} prefix={<SolutionOutlined style={{ color: '#7C3AED' }} />} /></Card></Col>
                <Col xs={12} sm={6}><Card style={{ borderRadius: '12px', border: '1px solid #E2E8F0' }}><Statistic title="Total Classes" value={stats.totalClasses} prefix={<HomeOutlined style={{ color: '#059669' }} />} /></Card></Col>
                <Col xs={12} sm={6}><Card style={{ borderRadius: '12px', border: '1px solid #E2E8F0' }}><Statistic title="Pending Inquiries" value={stats.pendingInquiries} prefix={<InboxOutlined style={{ color: '#EA580C' }} />} valueStyle={{ color: '#EA580C', fontWeight: 700 }} /></Card></Col>
            </Row>

            {/* Charts & Reports */}
            <Row gutter={[24, 24]}>
                <Col xs={24} lg={12}>
                    <Card title={<Space><BarChartOutlined style={{ color: primaryColor }} /><span>Attendance Analytics (Last 7 Days)</span></Space>} bordered={false} style={{ borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={stats.attendanceTrend} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                                <defs><linearGradient id="colorPrincipal" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={primaryColor} stopOpacity={0.8} /><stop offset="95%" stopColor={primaryColor} stopOpacity={0} /></linearGradient></defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                <XAxis dataKey="date" tickLine={false} axisLine={false} style={{ fontSize: '12px' }} />
                                <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tickFormatter={(value) => `${value}%`} style={{ fontSize: '12px' }} />
                                <Tooltip contentStyle={{ borderRadius: 8, borderColor: '#E2E8F0' }} formatter={(value) => [`${value}%`, 'Attendance']} />
                                <Area type="monotone" dataKey="percentage" stroke={primaryColor} fillOpacity={1} fill="url(#colorPrincipal)" strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Row gutter={[24, 24]}>
                        <Col xs={24} md={12}>
                            <Card title={<Text type="secondary" style={{ fontSize: 14 }}><CheckCircleOutlined /> Daily Attendance Avg</Text>} style={{ height: '100%', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                                <Progress type="circle" percent={stats.avgAttendance} width={100} strokeColor={primaryColor} style={{ display: 'block', margin: 'auto' }} />
                            </Card>
                        </Col>
                        <Col xs={24} md={12}>
                            <GenderBreakdownWidget breakdown={stats.genderBreakdown} />
                        </Col>
                        <Col xs={24}>
                            <RecentLoginsWidget logins={stats.recentLogins} />
                        </Col>
                    </Row>
                </Col>
            </Row>

            {/* Bottom Row - Top Students & Events */}
            <Row gutter={[24, 24]}>
                <Col xs={24} lg={12}>
                    <TopStudentsWidget
                        students={stats.topStudents}
                        classes={classes}
                        onClassChange={setSelectedClass}
                        selectedClass={selectedClass}
                    />
                </Col>
                <Col xs={24} lg={12}>
                    <UpcomingEventsWidget events={stats.recentEvents} />
                </Col>
            </Row>
            <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                <Col xs={24} lg={12}>
                    <BottomStudentsWidget students={stats.bottomStudents} />
                </Col>
            </Row>
        </Space>
    );

    // --- Super Admin Dashboard (Extends Principal) ---
    const SuperAdminDashboard = ({ stats }) => (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <PrincipalDashboard stats={stats} />
            <Title level={3} style={{ marginTop: 24, fontWeight: 700 }}><DatabaseOutlined style={{ marginRight: 8, color: '#64748B' }} /> System Health</Title>
            <Row gutter={[24, 24]}>
                <Col xs={24} md={8}>
                    <Card style={{ borderRadius: '12px', border: '1px solid #E2E8F0' }}><Statistic title="Database Version" value={stats.systemInfo.database_version} prefix={<GlobalOutlined style={{ color: '#64748B' }} />} /></Card>
                </Col>
                <Col xs={24} md={8}>
                    <Card style={{ borderRadius: '12px', border: '1px solid #E2E8F0' }}><Statistic title="DB Storage Size" value={stats.systemInfo.db_size} prefix={<UpSquareOutlined style={{ color: '#64748B' }} />} /></Card>
                </Col>
                <Col xs={24}>
                    <Card 
                        style={{ borderRadius: '12px', border: '1px solid #E2E8F0', backgroundColor: '#F8FAFC' }}
                        title={<Link to="/logs"><Space><DashOutlined style={{ color: '#64748B' }} /><Title level={4} style={{ margin: 0, fontWeight: 600 }}>System Audit Logs</Title></Space></Link>} 
                        bordered={false}
                    >
                        <Text type="secondary">Review all user actions and system events for security and compliance.</Text>
                    </Card>
                </Col>
            </Row>
        </Space>
    );

    // --- Main Render Logic ---

    const renderDashboard = () => {
        if (user.role === 'teacher') {
            return <TeacherDashboard stats={stats} user={user} />;
        }
        if (user.role === 'principal') {
            return <PrincipalDashboard stats={stats} />;
        }
        if (user.role === 'super_admin') {
            return <SuperAdminDashboard stats={stats} />;
        }
        return <Alert message="Access Denied" description="Your role does not have a defined dashboard view." type="error" showIcon />;
    };

    return (
        <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh', padding: isMobile ? '0' : '8px' }}>
            <div style={{ marginBottom: 32 }}>
                <Title level={2} style={{ fontWeight: 800, margin: 0 }}>
                    Overview
                </Title>
                <Text style={{ fontSize: '15px', color: '#64748B' }}>
                    Welcome back, <strong>{user?.username || 'User'}</strong>. Managing session: <strong>{currentYear?.year_name || 'N/A'}</strong>
                </Text>
            </div>

            {renderDashboard()}
        </div>
    );
};

export default DashboardPage;